import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../../components/SEOHead';
import HormuzHubNav from '../../../components/HormuzHubNav';
import { chokepointsAPI } from '../../../services/api';
import { getCollectionPageSchema, getTimelineEpisodeThingSchema } from '../../../utils/seoSchemas';
import { Clock } from 'lucide-react';

/** Normalize tags from API (array, JSON string, or null). */
function normalizeTimelineTags(tags) {
  if (tags == null) return [];
  if (Array.isArray(tags)) return tags;
  if (typeof tags === 'string') {
    try {
      const parsed = JSON.parse(tags);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return tags.trim() ? [tags] : [];
    }
  }
  return [];
}

const HormuzHistoricalTimeline = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await chokepointsAPI.getTimeline('hormuz');
        setEvents(res.data.events);
      } catch (e) {
        console.error('Failed to load timeline', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const title = 'Strait of Hormuz Historical Timeline | Incidents, Risk & Trade Impact';
  const description =
    'Analytical timeline of major incidents affecting the Strait of Hormuz, explaining what happened, why it matters, and how each event influenced global trade, shipping risk, and oil markets.';

  // Default events if none in database
  const defaultEvents = [
    {
      id: 1,
      title: 'Tanker War (1984-1988)',
      summary:
        'Iran-Iraq conflict led to attacks on oil tankers, disrupting shipping and causing significant oil price volatility.',
      created_at: '1984-01-01',
    },
    {
      id: 2,
      title: 'Operation Praying Mantis (1988)',
      summary:
        'US naval operation in response to Iranian mining of the strait, demonstrating international commitment to free navigation.',
      created_at: '1988-04-18',
    },
    {
      id: 3,
      title: '2019 Tanker Attacks',
      summary:
        'Multiple tanker attacks attributed to Iran raised concerns about shipping security and oil supply disruptions.',
      created_at: '2019-06-13',
    },
  ];

  const timelineEvents = events.length > 0 ? events : defaultEvents;

  const collectionPageItems = timelineEvents.map((event) => getTimelineEpisodeThingSchema(event));

  const jsonLd = [
    getCollectionPageSchema(
      'Strait of Hormuz Historical Timeline',
      description,
      collectionPageItems
    ),
  ];

  return (
    <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEOHead
        title={title}
        description={description}
        path="/strait-of-hormuz/historical-timeline"
        keywords="Hormuz history, Strait of Hormuz incidents, tanker war, Hormuz timeline"
        jsonLd={jsonLd}
        image="/api/og-image/hormuz-timeline.png"
        imageAlt="Strait of Hormuz Historical Timeline - Major incidents and events"
      />

      <HormuzHubNav />

      {/* A. Overview */}
      <header className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-on-surface mb-2">
          Historical Timeline
        </h1>
        <p className="text-xs text-slate-600 dark:text-on-surface-variant">
          Author: <span className="font-medium">TraderRadar Analytics Team</span>
        </p>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant mb-2">
          Last updated: March 2026 · This page summarises key historical episodes that helped shape
          how markets, insurers, and policymakers assess risk in the Strait of Hormuz.
        </p>
        <p className="text-sm text-slate-600 dark:text-on-surface-variant leading-relaxed">
          Each major incident in Hormuz leaves a trace in trade data: from tanker war attacks that
          re‑priced war‑risk insurance, to more recent episodes that shifted naval deployments and
          regulatory frameworks. Rather than listing dates, this timeline focuses on how events
          translated into shipping disruptions, oil price moves, and longer‑term changes in
          behaviour.
        </p>
      </header>

      {/* E. Intro: How to Read This Timeline */}
      <section className="mb-6 rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-3">
          How Hormuz Events Translate Into Trade Risk
        </h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant mb-3">
          For each event below we highlight three angles: what happened, why it mattered at the
          time, and how it affected global trade. Many episodes share a common pattern – a
          geopolitical trigger is followed by shipping incidents, insurance repricing, and finally
          broader adjustments in oil markets and trade policy.
        </p>
        <p className="text-xs text-slate-600 dark:text-on-surface-variant">
          For quantitative views of how much oil moves through Hormuz today, see the{' '}
          <Link
            to="/strait-of-hormuz/statistics"
            className="text-primary-600 dark:text-primary underline"
          >
            Statistics
          </Link>{' '}
          page. For scenario‑based estimates of how future disruptions could affect prices and
          growth, see the{' '}
          <Link
            to="/strait-of-hormuz/economic-impact"
            className="text-primary-600 dark:text-primary underline"
          >
            Economic Impact
          </Link>{' '}
          analysis.
        </p>
      </section>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
        </div>
      ) : (
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-300 dark:bg-surface-container-low" />

          <div className="space-y-8">
            {timelineEvents.map((event, idx) => {
              const tagList = normalizeTimelineTags(event.tags);
              return (
              <div key={event.id || idx} className="relative flex items-start">
                {/* Timeline dot */}
                <div className="absolute left-6 w-4 h-4 bg-primary-600 rounded-full border-4 border-white dark:border-gray-900 z-10" />

                {/* Event content */}
                <div className="ml-12 flex-1 rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-on-surface">
                      {event.title}
                    </h3>
                    {(event.event_date || event.created_at) && (
                      <time
                        dateTime={event.event_date || event.created_at}
                        className="text-sm text-slate-500 dark:text-on-surface-variant flex items-center"
                      >
                        <Clock className="w-4 h-4 mr-1" />
                        {new Date(event.event_date || event.created_at).getFullYear()}
                      </time>
                    )}
                  </div>
                  <div className="space-y-2 text-sm text-slate-700 dark:text-on-surface-variant">
                    <p>
                      <strong>What happened:</strong>{' '}
                      {event.summary || event.content || 'Incident affecting shipping or security in the strait.'}
                    </p>
                    <p>
                      <strong>Why it mattered:</strong>{' '}
                      {event.analysis_why ||
                        'The episode raised questions about the security of Gulf energy exports and prompted governments and shipowners to reassess routing and escort arrangements.'}
                    </p>
                    <p>
                      <strong>Impact on global trade:</strong>{' '}
                      {event.analysis_trade ||
                        'Markets responded through higher insurance premia, changes in freight rates, and heightened sensitivity of oil prices to further headlines from the region.'}
                    </p>
                  </div>
                  {tagList.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {tagList.map((tag, tagIdx) => (
                        <span
                          key={tagIdx}
                          className="px-2 py-1 bg-gray-100 dark:bg-gray-800 text-xs rounded text-slate-700 dark:text-on-surface-variant"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
            })}
          </div>
        </div>
      )}

      {/* Pattern Analysis and Links */}
      <section className="mt-10 bg-slate-100 dark:bg-surface-container-low rounded-lg border border-slate-200 dark:border-outline-variant/15 p-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-3">
          Patterns Across Hormuz Crises
        </h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant mb-3">
          Looking across several decades of incidents, a few recurring patterns stand out. These
          patterns help explain why even relatively small events can move markets and shipping
          decisions quickly.
        </p>
        <ul className="space-y-2 text-sm text-slate-700 dark:text-on-surface-variant list-disc list-inside mb-3">
          <li>
            Initial uncertainty about responsibility and intent often moves prices more than the
            physical damage itself, as traders price in the risk of escalation.
          </li>
          <li>
            Insurance and freight markets tend to react faster than long‑term trade flows, making
            them useful early indicators of perceived risk at the chokepoint.
          </li>
          <li>
            Over time, some measures introduced in response to crises – such as naval escorts or new
            reporting requirements – become part of the permanent operating environment.
          </li>
        </ul>
        <p className="text-xs text-slate-600 dark:text-on-surface-variant">
          For a live view of how current events are reflected in market indicators, visit the{' '}
          <Link
            to="/strait-of-hormuz/risk-dashboard"
            className="text-primary-600 dark:text-primary underline"
          >
            Risk Dashboard
          </Link>{' '}
          and the{' '}
          <Link
            to="/strait-of-hormuz/live-updates"
            className="text-primary-600 dark:text-primary underline"
          >
            Live Updates
          </Link>{' '}
          feed.
        </p>
      </section>

      {/* TraderRadar Intelligence Insight */}
      <section className="mt-8 mb-8 rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-3">
          TraderRadar Intelligence Insight
        </h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant mb-3">
          Looking across four decades of Hormuz incidents, the most important pattern is that markets
          react not only to the physical loss of barrels but to how each episode changes the perceived
          rules of the game. Events that signal a willingness to target commercial shipping – such as
          the Tanker War of the 1980s or the clustered attacks in 2019 – tend to leave a longer‑lasting
          imprint on insurance and freight pricing than narrowly military confrontations that are
          clearly ring‑fenced.
        </p>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant mb-3">
          A second recurring feature is that after a major scare, structural responses often compress
          future risk even as headlines fade. Examples include expanded naval escort arrangements,
          revised routing protocols, and investments in overland pipelines or diversified import
          terminals. Traders who track these shifts can distinguish between crises that permanently
          tighten effective capacity and those that mainly re‑price risk for a single season.
        </p>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant mb-3">
          Finally, Hormuz events frequently act as stress tests for policy credibility. When a shock
          occurs in an already tight market, central banks and finance ministries must signal whether
          they will tolerate a temporary inflation spike or lean harder against it. The combination of
          energy market tightness and perceived policy constraints is often a better predictor of
          medium‑term macro impact than the raw severity of any one incident.
        </p>
        <div className="mt-4 overflow-x-auto rounded-lg border bg-slate-100 dark:bg-surface-container-low">
          <table className="min-w-full text-xs">
            <thead>
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-200">
                  Episode
                </th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-200">
                  Primary trigger
                </th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-200">
                  First market to react
                </th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-200">
                  Lasting structural change
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              <tr>
                <td className="px-3 py-2 text-gray-900 dark:text-gray-100">Tanker War (1980s)</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">Attacks on commercial shipping</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  War‑risk insurance premia and freight rates
                </td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  Normalisation of naval escorts and higher baseline insurance requirements
                </td>
              </tr>
              <tr>
                <td className="px-3 py-2 text-gray-900 dark:text-gray-100">2019 tanker attacks</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">Clustered sabotage incidents near key routes</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  Spot freight and options implied volatility
                </td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  Tighter monitoring of AIS gaps, route selection, and cargo ownership patterns
                </td>
              </tr>
              <tr>
                <td className="px-3 py-2 text-gray-900 dark:text-gray-100">Occasional drone &amp; missile incidents</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">Spillover from regional conflicts</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  Nearby benchmark spreads and regional credit markets
                </td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  Greater focus on hardening critical infrastructure and diversifying export routes
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </article>
  );
};

export default HormuzHistoricalTimeline;

